import { Link, TableCell, TableRow } from '@ksyos/design-system';
import type { ActivityType, EpisodeCategory, ServiceType } from '@common/enums';
import type { ICompensationPayoutLog } from '@frontend/components/interfaces/compensationPayoutLog';
import { CompensationPayoutLevel, ManagerAuthorization, ModificationType } from '@common/enums';
import { buildName } from '@common/utils/buildName';
import { never } from '@common/utils/never';
import { FormatCurrency } from '@frontend/components/elements/FormatCurrency';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';

interface IActivityCompensationPayoutsLogProps {
    compensationPayoutsLog: ICompensationPayoutLog;
    compensationType: ActivityType;
    employmentId?: string;
    episodeCategory: EpisodeCategory;
    groupId?: string;
    organizationId?: string;
    serviceGroupSettingId?: string;
    serviceName: string;
    serviceType?: ServiceType;
}

export function ActivityCompensationPayoutsLogEntry(props: IActivityCompensationPayoutsLogProps) {
    const language = getSelectedLanguage();

    const modifierName = props.compensationPayoutsLog.modifierPractitionerId
        ? buildName(
              props.compensationPayoutsLog.modifierPractitioner?.nameGiven ?? null,
              props.compensationPayoutsLog.modifierPractitioner?.nameFamily ?? null
          )
        : 'Via query';

    const message = buildMessage();

    return (
        <TableRow key={props.compensationPayoutsLog.id}>
            <TableCell>
                <NullableFormattedDate value={props.compensationPayoutsLog.created_at} />
            </TableCell>
            <TableCell>{modifierName}</TableCell>
            <TableCell>{message}</TableCell>
        </TableRow>
    );

    function buildMessage() {
        const { modificationType } = props.compensationPayoutsLog;

        switch (modificationType) {
            case ModificationType.insert:
            case ModificationType.update:
                if (props.compensationPayoutsLog.amount === null) {
                    return null;
                }

                return (
                    <>
                        Vergoeding {props.serviceName} ingesteld op {getPayoutLevel()}
                        {modificationType === ModificationType.update && ' gewijzigd '}
                        {props.compensationPayoutsLog.profession && (
                            <> voor beroep {language.professionEnum[props.compensationPayoutsLog.profession]}</>
                        )}{' '}
                        (<FormatCurrency value={props.compensationPayoutsLog.amount}></FormatCurrency>) vanaf{' '}
                        {<NullableFormattedDate value={props.compensationPayoutsLog.startDate} />}
                        {props.compensationPayoutsLog.endDate && (
                            <>
                                {' '}
                                tot <NullableFormattedDate value={props.compensationPayoutsLog.endDate} />
                            </>
                        )}
                    </>
                );

            case ModificationType.delete:
                return (
                    <>
                        Vergoeding {props.serviceName} ingesteld op {getPayoutLevel()}
                        {props.compensationPayoutsLog.profession && (
                            <> voor beroep {language.professionEnum[props.compensationPayoutsLog.profession]}</>
                        )}{' '}
                        is verwijderd
                    </>
                );

            default:
                never(modificationType);
        }
    }

    function getPayoutDetailLink() {
        let payoutDetailLink = '#';
        if (props.compensationPayoutsLog.level === CompensationPayoutLevel.employment && props.employmentId) {
            payoutDetailLink = `/employments/${props.employmentId}/personalSettings/compensationPayouts/${props.episodeCategory}`;
        }
        if (props.compensationPayoutsLog.level === CompensationPayoutLevel.organization && props.organizationId) {
            payoutDetailLink = `/organizations/${props.organizationId}/view/compensationPayouts/${props.episodeCategory}`;
        }
        if (
            props.compensationPayoutsLog.level === CompensationPayoutLevel.group &&
            props.serviceGroupSettingId &&
            props.groupId
        ) {
            payoutDetailLink = `/serviceGroupSettings/${props.serviceGroupSettingId}?groupId=${props.groupId}`;
        }

        return payoutDetailLink;
    }

    function getPayoutLevelName() {
        let payoutlevel = '';
        if (props.compensationPayoutsLog.level === CompensationPayoutLevel.employment) {
            payoutlevel = language.compensationPayoutLevels[CompensationPayoutLevel.employment];
        } else if (props.compensationPayoutsLog.level === CompensationPayoutLevel.organization) {
            payoutlevel = language.compensationPayoutLevels[CompensationPayoutLevel.organization];
        } else if (props.compensationPayoutsLog.level === CompensationPayoutLevel.group) {
            payoutlevel = language.compensationPayoutLevels[CompensationPayoutLevel.group];
        }

        return payoutlevel;
    }

    function getPayoutLevel() {
        const isClickable = !!(
            [
                [CompensationPayoutLevel.group, ManagerAuthorization.groupRead],
                [CompensationPayoutLevel.organization, ManagerAuthorization.organizationRead],
                [CompensationPayoutLevel.employment, ManagerAuthorization.employmentSettingsRead],
            ] as [CompensationPayoutLevel, ManagerAuthorization][]
        ).find((entry) => props.compensationPayoutsLog.level === entry[0] && hasOneAuthorization(entry[1]));

        return isClickable ? (
            <Link
                color="subtle"
                to={getPayoutDetailLink()}
                variant="link"
            >
                {getPayoutLevelName()}
            </Link>
        ) : (
            getPayoutLevelName()
        );
    }
}
